import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import './Operations.css';

function SinglesConverter() {
  const { sku } = useParams();
  const [quantity, setQuantity] = useState(6); // Default to 6 singles (1 case worth)
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState(null);
  const [selectedBin, setSelectedBin] = useState(null);
  const [destinationBin, setDestinationBin] = useState(null);
  const [suggestedBins, setSuggestedBins] = useState(null);
  const [manualBinEntry, setManualBinEntry] = useState(false);
  const [manualBinCode, setManualBinCode] = useState('');
  const [conversionResult, setConversionResult] = useState(null);
  const [isValidSinglesSku, setIsValidSinglesSku] = useState(false);

  // Determine the singles SKU from the provided SKU
  const getSinglesSku = (inputSku) => {
    if (!inputSku) return null;

    // If it's already a singles SKU (has '1' after prefix)
    if (inputSku.match(/^[A-Z]+1/i)) {
      return inputSku.toUpperCase();
    }

    // If it's a case SKU (has '2' after prefix), convert to singles
    if (inputSku.match(/^[A-Z]+2/i)) {
      return inputSku.replace(/^([A-Z]+)2/i, '$11').toUpperCase();
    }

    return null;
  };

  const singlesSku = getSinglesSku(sku);
  const caseSku = singlesSku ? singlesSku.replace(/^([A-Z]+)1/, '$12') : null;

  useEffect(() => {
    if (singlesSku) {
      setIsValidSinglesSku(true);
      fetchInventory();
      fetchSuggestedBins();
    } else {
      setIsValidSinglesSku(false);
      setLoading(false);
    }
  }, [singlesSku]);

  const fetchSuggestedBins = async () => {
    if (!caseSku) return;

    try {
      // Fetch suggested bins for the case SKU (where cases would go)
      const response = await axios.get(`/api/ops/suggested-bins/${caseSku}`);
      setSuggestedBins(response.data);
    } catch (error) {
      console.error('Failed to fetch suggested bins:', error);
    }
  };

  const fetchInventory = async () => {
    if (!singlesSku) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`/api/ops/inventory/${singlesSku}`);

      if (response.data && response.data.length > 0) {
        setInventory(response.data);
      } else {
        setInventory(null);
      }
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      setInventory(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBinSelection = (bin) => {
    setSelectedBin(bin);
    setDestinationBin(null);
    setManualBinCode('');
    setManualBinEntry(false);
  };

  const handleDestinationSelection = (bin) => {
    setDestinationBin(bin);
    setManualBinCode('');
    setManualBinEntry(false);
  };

  const handleConversion = async () => {
    if (!selectedBin || quantity < 1) return;

    // Determine destination bin ID
    let destBinId = selectedBin.bin_id; // Default to same bin
    if (destinationBin) {
      destBinId = destinationBin.id || destinationBin.bin_id;
    } else if (manualBinEntry && manualBinCode) {
      destBinId = manualBinCode;
    }

    setLoading(true);
    setConversionResult(null);

    try {
      const response = await axios.post('/api/ops/convert-singles', {
        singles_sku: singlesSku,
        bin_id: selectedBin.bin_id,
        destination_bin_id: destBinId,
        quantity: quantity,
        user: localStorage.getItem('userName') || 'Mobile User'
      });

      setConversionResult(response.data);

      // Reset form
      setQuantity(6);
      setSelectedBin(null);
      setDestinationBin(null);
      setManualBinCode('');
      setManualBinEntry(false);

      // Refresh inventory
      await fetchInventory();
      await fetchSuggestedBins();
    } catch (error) {
      console.error('Conversion failed:', error);
      alert(error.response?.data?.error || 'Conversion failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isValidSinglesSku) {
    return (
      <div className="ops-page">
        <div className="ops-page-header">
          <Link to={`/ops/${sku}`} className="back-button">← Back to Menu</Link>
          <h1>Singles to Cases Conversion</h1>
        </div>

        <div className="error-message">
          <h3>Invalid SKU Format</h3>
          <p>The SKU "{sku}" cannot be converted to singles format.</p>
          <p>Singles SKUs should have "1" after the prefix (e.g., RW1, CF1)</p>
          <Link to={`/ops/${sku}`} className="submit-button">
            Back to Operations Menu
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ops-page">
        <div className="ops-page-header">
          <Link to={`/ops/${sku}`} className="back-button">← Back to Menu</Link>
          <h1>Singles to Cases Conversion</h1>
          <div className="sku-label">Singles: {singlesSku} → Case: {caseSku}</div>
        </div>
        <div className="loading-inline">Loading inventory...</div>
      </div>
    );
  }

  if (!inventory || inventory.length === 0) {
    return (
      <div className="ops-page">
        <div className="ops-page-header">
          <Link to={`/ops/${sku}`} className="back-button">← Back to Menu</Link>
          <h1>Singles to Cases Conversion</h1>
          <div className="sku-label">Singles: {singlesSku} → Case: {caseSku}</div>
        </div>

        <div className="error-message">
          <h3>No Inventory Found</h3>
          <p>No singles inventory found for {singlesSku}</p>
          <Link to={`/ops/${sku}`} className="submit-button">
            Back to Operations Menu
          </Link>
        </div>
      </div>
    );
  }

  const totalQuantity = inventory.reduce((sum, bin) => sum + bin.quantity, 0);
  const casesCanMake = Math.floor(totalQuantity / 6); // Assuming 6 singles per case

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <Link to={`/ops/${sku}`} className="back-button">← Back to Menu</Link>
        <h1>Singles to Cases Conversion</h1>
        <div className="sku-label">Singles: {singlesSku} → Case: {caseSku}</div>
        <div className="total-qty">Total Singles: {totalQuantity} (Can make {casesCanMake} cases)</div>
      </div>

      <div className="conversion-form">
        <div className="form-group">
          <label>Select Source Bin (Singles Location)</label>
          <div className="bin-selection">
            {inventory.map(bin => (
              <div
                key={bin.bin_id}
                className={`bin-option ${selectedBin?.bin_id === bin.bin_id ? 'selected' : ''}`}
                onClick={() => handleBinSelection(bin)}
              >
                <div className="bin-option-header">
                  <input
                    type="radio"
                    checked={selectedBin?.bin_id === bin.bin_id}
                    onChange={() => handleBinSelection(bin)}
                  />
                  <div className="bin-code">{bin.bin_code}</div>
                </div>
                <div className="bin-option-details">
                  <span>{bin.quantity} singles</span>
                  <span>{Math.floor(bin.quantity / 6)} cases possible</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedBin && (
          <>
            <div className="form-group">
              <label htmlFor="quantity">Number of Singles to Convert</label>
              <input
                type="number"
                id="quantity"
                className="form-control"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                min="1"
                max={selectedBin.quantity}
                step="1"
              />
              <span className="form-help">
                Will create {Math.floor(quantity / 6)} case(s)
                {quantity % 6 > 0 && ` with ${quantity % 6} singles remaining`}
              </span>
              {quantity < 6 && quantity > 0 && (
                <span className="form-warning">
                  Need at least 6 singles to make 1 case
                </span>
              )}
            </div>

            <div className="form-group">
              <label>Select Destination Bin (Where to put cases)</label>

              {suggestedBins && suggestedBins.binsWithItem && suggestedBins.binsWithItem.length > 0 && (
                <>
                  <div className="bin-group-label">Bins with existing cases</div>
                  {suggestedBins.binsWithItem.map(bin => (
                    <div
                      key={bin.id}
                      className={`bin-option ${destinationBin?.id === bin.id ? 'selected' : ''}`}
                      onClick={() => handleDestinationSelection(bin)}
                    >
                      <div className="bin-option-header">
                        <input
                          type="radio"
                          name="destination"
                          checked={destinationBin?.id === bin.id}
                          onChange={() => handleDestinationSelection(bin)}
                        />
                        <div className="bin-code">{bin.name}</div>
                        <div className="bin-badge preferred">Has Cases</div>
                      </div>
                      <div className="bin-option-details">
                        <span>{bin.currentQty} cases currently</span>
                      </div>
                    </div>
                  ))}
                </>
              )}

              <div className="bin-option" onClick={() => {
                setDestinationBin(null);
                setManualBinEntry(!manualBinEntry);
              }}>
                <div className="bin-option-header">
                  <input
                    type="radio"
                    name="destination"
                    checked={manualBinEntry}
                    onChange={() => {
                      setDestinationBin(null);
                      setManualBinEntry(true);
                    }}
                  />
                  <div className="bin-code">Enter bin manually</div>
                </div>
                {manualBinEntry && (
                  <div className="manual-bin-input">
                    <input
                      type="text"
                      value={manualBinCode}
                      onChange={(e) => setManualBinCode(e.target.value.toUpperCase())}
                      placeholder="Enter bin code (e.g., 9-1-A)"
                      autoFocus
                    />
                  </div>
                )}
              </div>

              <div className="bin-option" onClick={() => {
                setDestinationBin(null);
                setManualBinEntry(false);
                setManualBinCode('');
              }}>
                <div className="bin-option-header">
                  <input
                    type="radio"
                    name="destination"
                    checked={!destinationBin && !manualBinEntry}
                    onChange={() => {
                      setDestinationBin(null);
                      setManualBinEntry(false);
                      setManualBinCode('');
                    }}
                  />
                  <div className="bin-code">Keep in same bin ({selectedBin.bin_code})</div>
                </div>
              </div>
            </div>

            <button
              className="convert-action-button"
              onClick={handleConversion}
              disabled={loading || quantity < 6 || (manualBinEntry && !manualBinCode)}
            >
              {loading ? 'Converting...' : `Convert ${quantity} Singles to ${Math.floor(quantity / 6)} Case(s)`}
            </button>
          </>
        )}
      </div>

      {conversionResult && (
        <div className="success-message">
          <h3>✓ Conversion Successful!</h3>
          <div className="result-details">
            <p>{conversionResult.message}</p>
            {conversionResult.details && (
              <ul>
                <li>Singles Used: {conversionResult.details.singlesUsed}</li>
                <li>Cases Created: {conversionResult.details.casesCreated}</li>
                {conversionResult.details.remainingSingles > 0 && (
                  <li>Singles Remaining: {conversionResult.details.remainingSingles}</li>
                )}
                <li>Destination: {conversionResult.details.destinationBin}</li>
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SinglesConverter;